import React, { BaseSyntheticEvent, useEffect, useState } from 'react'
import {
  IonContent,
  IonPage,
  IonList,
  IonLabel,
  IonItemDivider,
  IonItem,
  IonButtons,
  IonButton,
  IonIcon,
  IonModal,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonGrid,
  IonRow,
  IonHeader,
  IonToolbar,
  IonTitle,
  useIonViewWillEnter,
  useIonViewDidLeave,
  IonSpinner,
  IonCol,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import ScanDriversLicenseButton from '../../driversLicenseScanner/scanDriversLicenseButton/scanDriversLicenseButton.component'
import { useDispatch, useSelector } from 'react-redux'
import AddSignatureButton from '../../signatures/addSignatureButton/addSignatureButton.component'
import { codeDownload, remove, trash } from 'ionicons/icons'
import './Attachments.page.css'
import {
  deleteAttachment,
  deleteSignatureType,
  setAcknowledgePrivacyNotice,
  setSignatureChips,
  showConfirmationAlert,
  showAttachments as showAttachmentsAction,
  saveSignaturesPcr,
  getAttachments,
} from '../../signatures/store/signatures.actions'
import { getSignaturesSelector } from '../../signatures/selectors/getSignatures.selector'
import { IImageAttachment } from '../../signatures/store/signatures.types'
import { showAlert } from '../../../components/alert/store/alert.actions'
import ImageComponent from '../../../components/image/Image.component'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { getCustomSignaturesSelector } from '../../customSignature/selectors/getCustomSignature.selectors'
import { IDelteChipId, IPrFormSignatureState } from '../../customSignature/store/customSignature.types'
import { setDeleteChipId } from '../../customSignature/store/customSignature.actions'
import { getCameraAttachmentsSelector } from '../../cameraAttachments/selectors'
import CameraButton from '../../cameraAttachments/cameraButton/cameraButton.component'
import {
  getConfigurationOptionsSelector,
  getFillablePDFOptionsSelector,
  getSignatureOptionsSelector,
} from '../../options/selectors'
import { PhotoViewer } from '@capacitor-community/photoviewer'
import { getEKGImportSelector } from '../../ekgImport/selectors'
import ReportAttachment from '../../ekgImport/reportAttachment/ReportAttachment.component'
import { deleteReportAttachment, getReportAttachments } from '../../ekgImport/store/ekgImport.actions'
import { IReportAttachment } from '../../ekgImport/store/ekgImport.types'
import EKGImportButton from '../../ekgImport/ekgImportButton/ekgImportButton.component'
import { EKG_IMPORT_MODULE_ID } from '../../ekgImport/store/ekgImport.reducers'
import { SPECIAL_REPORTS_MODULE_ID } from '../../specialReports/store/specialReports.reducers'
import { useOfflineStatus } from '../../../utilities/isOnline.util'
import ScanDocumentButton from '../../documentScanner/scanDocumentButton/ScanDocumentButton.component'
import { getDocumentScannerSelector } from '../../documentScanner/selectors/documentScanner.selector'
import DocumentAttachment from '../../documentScanner/documentAttachment/DocumentAttachment.component'
import { IDocumentAttachment } from '../../documentScanner/store/documentScanner.types'
import { deleteDocumentAttachment, getDocumentAttachments } from '../../documentScanner/store/documentScanner.actions'
import { getMaximumDocumentAttachments } from '../../documentScanner/store/documentScanner.reducer'
import { isPlatformNative } from '../../../utilities/platform.util'
import { Capacitor } from '@capacitor/core'
import { close, documentTextOutline } from 'ionicons/icons'
import Logger from '../../../services/logger.service'
import SpecialReportButton from '../../specialReports/specialReportButton/SpecialReportButton.component'
import { ISpecialReport } from '../../specialReports/store/specialReports.types'
import { deleteSpecialReport, getSpecialReports } from '../../specialReports/store/specialReports.actions'
import { getSpecialReportsSelector } from '../../specialReports/selectors'
import FillablePDFButton from '../../fillablePDFs/fillablePDFsButton/FillablePDFsButton.component'
import FillablePDFsModal from '../../fillablePDFs/fillablePDFsModal/FillablePDFsModal.component'
import { getFillablePDFsSelector } from '../../fillablePDFs/selectors'
import { deleteFillablePDF } from '../../fillablePDFs/store/fillablePDFs.actions'
import { IPDFListItem } from '../../fillablePDFs/store/fillablePDFs.types'
import { getPulsaraAttachments } from '../../pulsara/store/pulsara.actions'
import PulsaraAttachment from '../../pulsara/content/importStep/PulsaraAttachment.component'

export interface IShowPhotoViewerProps {
  src?: string
  alt?: string
}

const Attachments: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isOfflineStatus = useOfflineStatus()

  const signaturesState = useSelector(getSignaturesSelector)
  const { attachments, chips, showAttachments } = signaturesState
  const customSignaturesState = useSelector(getCustomSignaturesSelector)
  const { prFormsSignature } = customSignaturesState
  const cameraAttachmentsState = useSelector(getCameraAttachmentsSelector)
  const { cameraAttachments } = cameraAttachmentsState
  const { reportAttachments, monitorOptions } = useSelector(getEKGImportSelector)

  const { documentAttachments, disableScanButton } = useSelector(getDocumentScannerSelector)

  const signatureOptionsState = useSelector(getSignatureOptionsSelector)
  const { customSignatureOptions, isStandardSignatureConfigured } = signatureOptionsState

  const configurationOptions = useSelector(getConfigurationOptionsSelector)
  const { serviceModules } = configurationOptions

  const [isLoading, setIsLoading] = useState(true)
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false)
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null)

  const specialReportsState = useSelector(getSpecialReportsSelector)
  const { specialReportsList } = specialReportsState

  const fillablePDFsState = useSelector(getFillablePDFsSelector)
  const { showModal, fillablePDFsList } = fillablePDFsState

  const fillablePDFOptionState = useSelector(getFillablePDFOptionsSelector)
  const { metadata } = fillablePDFOptionState

  const showCloudEkg =
    !isOfflineStatus &&
    serviceModules.find((i: { module_id: number }) => i.module_id === EKG_IMPORT_MODULE_ID) &&
    monitorOptions.length > 1

  const showSpecialReports = serviceModules.some(
    (i: { module_id: number }) => i.module_id === SPECIAL_REPORTS_MODULE_ID
  )

  const isOnNativePlatform = isPlatformNative()

  useIonViewWillEnter(() => {
    dispatch(getAttachments())
    dispatch(getReportAttachments())
    dispatch(getDocumentAttachments())
    dispatch(getSpecialReports())
    dispatch(getPulsaraAttachments())
  })

  useIonViewDidLeave(() => {
    setIsLoading(true)
  })

  useEffect(() => {
    // Using this as a proxy for whether the redux state loaded
    const didReduxLoad = !disableScanButton || documentAttachments.length === getMaximumDocumentAttachments()

    if (didReduxLoad) {
      setIsLoading(false)
    }
  }, [disableScanButton, documentAttachments])

  const clear = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    dispatch(showAttachmentsAction(false))
    dispatch(saveSignaturesPcr())
  }

  const download = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    dispatch(getAttachments())
    dispatch(showAttachmentsAction(true))
  }

  // const onFileSelection = (e: BaseSyntheticEvent) => {
  //   e.preventDefault()
  //   dispatch(setFileName(e.target.value))
  // }

  const onDeleteClick = (item: IImageAttachment, type: string) => {
    const attachmentID = type === 'signature' ? item.key : item.id
    dispatch(showConfirmationAlert(attachmentID, type))
    const alertState = {
      header: 'Delete Attachment',
      message: 'Are you sure you want to delete this attachment?',
      buttons: [
        { text: 'Cancel' },
        { text: 'Ok', role: 'destructive', handler: () => handleStandardDelete(type, attachmentID) },
      ],
    }
    dispatch(showAlert(alertState))
  }

  const onReportAttachmentDeleteClick = (reportAttachment: IReportAttachment) => {
    const alertState = {
      header: 'Delete Attachment',
      message: 'Are you sure you want to delete this attachment?',
      buttons: [
        { text: 'Cancel' },
        { text: 'Ok', role: 'destructive', handler: () => handleDeleteReportAttachment(reportAttachment) },
      ],
    }
    dispatch(showAlert(alertState))
  }

  const onDocumentAttachmentDelete = (documentAttachment: IDocumentAttachment) => {
    const alertState = {
      header: 'Delete Attachment',
      message: 'Are you sure you want to delete this attachment?',
      buttons: [
        { text: 'Cancel' },
        { text: 'Ok', role: 'destructive', handler: () => handleDeleteDocumentAttachment(documentAttachment) },
      ],
    }
    dispatch(showAlert(alertState))
  }

  const onCustomDeleteClick = (item: IPrFormSignatureState) => {
    const customSeqTemp = {
      formId: item.formId,
      signatureId: item.signatureId,
    }
    const alertState = {
      header: t('signature.alert.deleteAttachment.header'),
      message: t('signature.alert.deleteAttachment.message'),
      buttons: [
        { text: 'Cancel' },
        { text: 'Ok', role: 'destructive', handler: () => handleCustomDelete(customSeqTemp) },
      ],
    }
    dispatch(showAlert(alertState))
  }

  const onPDFDeleteClick = (pdf: string) => {
    const alertState = {
      header: 'Delete PDF',
      message: 'Are you sure you want to delete this PDF?',
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: () => handleDeletePDF(pdf) }],
    }
    dispatch(showAlert(alertState))
  }

  const handleStandardDelete = (attachmentTypeTemp: string, attachmentIdTemp: string | undefined) => {
    if (attachmentTypeTemp === 'signature') {
      const chipsArr = [...chips]
      const sign = attachmentIdTemp?.split('_')[2]
      const chipDataIndex = chipsArr.findIndex((chipObject) => sign === chipObject?.option?.signatureType)
      if (chipDataIndex >= 0) {
        dispatch(deleteSignatureType(chipsArr[chipDataIndex].option)) // moved here to resolve persistance of deleted data in pcrDoc
        chipsArr.splice(chipDataIndex, 1)
        dispatch(setSignatureChips(chipsArr))
      }
      dispatch(deleteAttachment())
      chipsArr.length === 0 && dispatch(setAcknowledgePrivacyNotice(false))
    } else {
      dispatch(deleteAttachment())
    }
  }

  const handleCustomDelete = (customSeqTemp: IDelteChipId) => {
    if (customSeqTemp?.signatureId) {
      dispatch(setDeleteChipId(customSeqTemp))
    } else {
      dispatch(deleteAttachment())
    }
  }

  const handleDeleteReportAttachment = (reportAttachment: IReportAttachment) => {
    dispatch(deleteReportAttachment(reportAttachment.id || null))
  }

  const handleDeleteDocumentAttachment = (documentAttachment: IDocumentAttachment) => {
    dispatch(deleteDocumentAttachment(documentAttachment.id || null))
  }

  const handleDeletePDF = (pdf: string) => {
    dispatch(deleteFillablePDF(pdf || null))
  }

  const stringConvert = (name: string) => {
    const strArr = name.split(' ')
    return strArr.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join('')
  }
  const showPhotoViewer = (photoViewerProps: IShowPhotoViewerProps) => {
    const { src, alt } = photoViewerProps
    if (!src) return

    try {
      const isNative = Capacitor.isNativePlatform()
      if (isNative) {
        // Use the PhotoViewer plugin for native devices
        PhotoViewer.show({
          images: [{ url: src || '', title: alt }],
          options: { share: false },
        })
      } else {
        // Show the image in a modal for web instead of opening a new window
        setSelectedPhoto(src)
        setIsPhotoViewerOpen(true)
      }
    } catch (error) {
      Logger.error('Error opening PhotoViewer:' + error)
      dispatch(
        showAlert({
          header: 'Error',
          message: 'Unable to open image viewer',
          buttons: ['OK'],
        })
      )
    }
  }

  const handleSpecialReportDelete = (specialReport: ISpecialReport) => {
    dispatch(deleteSpecialReport(specialReport.id))
  }

  const specialReportDeleteAlert = (specialReport: ISpecialReport) => {
    const alertState = {
      header: 'Delete Attachment',
      message: 'Are you sure you want to delete this attachment?',
      buttons: [
        { text: 'Cancel' },
        { text: 'Ok', role: 'destructive', handler: () => handleSpecialReportDelete(specialReport) },
      ],
    }
    dispatch(showAlert(alertState))
  }

  const renderLoader = () => {
    return (
      <IonList>
        <IonSpinner className='attachments-loader' />
      </IonList>
    )
  }

  const renderAttachmentsContent = () => {
    return (
      <IonList>
        {showSpecialReports && showAttachments && specialReportsList.length > 0 && (
          <>
            <IonItemDivider color='light' className='ion-padding attachment-sub-heading'>
              <IonLabel className='grid-item-divider-label' color='medium'>
                {t('attachments.SpecialReports')}
              </IonLabel>
            </IonItemDivider>
            {specialReportsList.map((specialReport: ISpecialReport) => (
              <IonItemSliding key={`sr-slide-${specialReport.id}`}>
                <IonItem key={`sr-item-${specialReport.id}`}>
                  <IonIcon slot='start' icon={documentTextOutline} className='m-0 mr-4' />
                  <IonRow className='w-100'>
                    <IonCol size='12' className={specialReport.subject ? 'pb-0' : ''}>
                      <IonLabel>
                        {specialReport.categories && specialReport.categories.length > 0
                          ? specialReport.categories[0].name
                          : t('attachments.SpecialReport')}
                      </IonLabel>
                    </IonCol>
                    {specialReport.subject ? (
                      <IonCol size='12'>
                        <IonLabel className='sub-report-label' color='medium'>
                          {specialReport.subject}
                        </IonLabel>
                      </IonCol>
                    ) : null}
                  </IonRow>
                </IonItem>
                <IonItemOptions>
                  <IonItemOption color='danger' onClick={(e) => specialReportDeleteAlert(specialReport)}>
                    <IonIcon slot='start' icon={trash} />
                    {t('common.buttons.Delete')}
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </>
        )}
        {showAttachments && attachments.length > 0 && (
          <>
            <IonItemDivider color='light' className='ion-padding attachment-sub-heading'>
              <IonLabel className='grid-item-divider-label' color='medium'>
                {t('attachments.Signatures')}
              </IonLabel>
            </IonItemDivider>
            {attachments.map((item: IImageAttachment) => (
              <IonItemSliding key={item.key}>
                <IonItem data-testid={item.key} key={item.key}>
                  <IonGrid className='saved-signature-background'>
                    <IonRow>
                      <ImageComponent className='saved-signature' isPHI src={item.imageSrc} alt={item.key} />
                    </IonRow>
                    <IonRow>
                      <IonLabel className='overflowing-content'>{item.key}</IonLabel>
                    </IonRow>
                  </IonGrid>
                </IonItem>
                <IonItemOptions>
                  <IonItemOption color='danger' onClick={(e) => onDeleteClick(item, 'signature')}>
                    <IonIcon slot='start' icon={trash} />
                    {t('common.buttons.Delete')}
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </>
        )}
        {showAttachments && prFormsSignature.length > 0 && (
          <>
            <IonItemDivider color='light' className='ion-padding attachment-sub-heading'>
              <IonLabel className='grid-item-divider-label' color='medium'>
                {t('attachments.Signatures')}
              </IonLabel>
            </IonItemDivider>
            {prFormsSignature.map((item: IPrFormSignatureState) => {
              const name = 'CustomSignatures_' + stringConvert(item.__parentName) + '_' + item.__name + '_' + item.seq
              return (
                <IonItemSliding key={name}>
                  <IonItem data-testid={name} key={name}>
                    <IonGrid className='saved-signature-background'>
                      <IonRow>
                        <ImageComponent
                          className='saved-signature'
                          isPHI
                          src={`data:image/png;base64,${item.signature}`}
                          alt={name}
                        />
                      </IonRow>
                      <IonRow>
                        <IonLabel className='overflowing-content'>{name}</IonLabel>
                      </IonRow>
                    </IonGrid>
                  </IonItem>
                  <IonItemOptions>
                    <IonItemOption color='danger' onClick={(e) => onCustomDeleteClick(item)}>
                      <IonIcon slot='start' icon={trash} />
                      {t('common.buttons.Delete')}
                    </IonItemOption>
                  </IonItemOptions>
                </IonItemSliding>
              )
            })}
          </>
        )}
        {showAttachments && cameraAttachments.length > 0 && (
          <>
            <IonItemDivider color='light' className='ion-padding attachment-sub-heading'>
              <IonLabel className='grid-item-divider-label' color='medium'>
                {t('attachments.CameraAttachments')}
              </IonLabel>
            </IonItemDivider>
            {cameraAttachments.map((item: IImageAttachment, index: number) => (
              <IonItemSliding key={item.id}>
                <IonItem data-testid={`CameraAttachment${item.id}`} key={item.id}>
                  <IonGrid className='camera-attachments-grid'>
                    <IonRow>
                      <ImageComponent
                        className='saved-signature'
                        isPHI
                        src={`data:image/png;base64,${item.data}`}
                        alt={item.name}
                        onClickAction={showPhotoViewer}
                      />
                      {/* Modal for Web Photo Viewer */}
                      <IonModal isOpen={isPhotoViewerOpen} onDidDismiss={() => setIsPhotoViewerOpen(false)}>
                        <div className='centered-container'>
                          <img className='img-width-height' src={selectedPhoto || ''} alt='Preview' />
                          <IonButton
                            className='absolute-top-right'
                            fill='clear'
                            color='light'
                            onClick={() => setIsPhotoViewerOpen(false)}>
                            <IonIcon icon={close} size='large' />
                          </IonButton>
                        </div>
                      </IonModal>
                    </IonRow>
                    <IonRow className='wrap-text'>
                      <IonLabel>{`${item.name}${item.category ? `, ${item.category.name}` : ''}`}</IonLabel>
                    </IonRow>
                  </IonGrid>
                </IonItem>
                <IonItemOptions>
                  <IonItemOption color='danger' onClick={(e) => onDeleteClick(item, 'cameraAttachment')}>
                    <IonIcon slot='start' icon={trash} />
                    {t('common.buttons.Delete')}
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </>
        )}
        {showAttachments && documentAttachments.length > 0 && (
          <>
            <IonItemDivider color='light' className='ion-padding attachment-sub-heading'>
              <IonLabel className='grid-item-divider-label' color='medium'>
                {t('attachments.DocumentAttachments')}
              </IonLabel>
            </IonItemDivider>
            {documentAttachments.map((documentAttachment: IDocumentAttachment, index: number) => (
              <IonItemSliding key={documentAttachment.id}>
                <DocumentAttachment key={index} document={documentAttachment} />
                <IonItemOptions>
                  <IonItemOption color='danger' onClick={(e) => onDocumentAttachmentDelete(documentAttachment)}>
                    <IonIcon slot='start' icon={trash} />
                    {t('common.buttons.Delete')}
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </>
        )}
        {showAttachments && reportAttachments.length > 0 && (
          <>
            <IonItemDivider color='light' className='ion-padding attachment-sub-heading'>
              <IonLabel className='grid-item-divider-label' color='medium'>
                {t('attachments.ReportAttachments')}
              </IonLabel>
            </IonItemDivider>
            {reportAttachments.map((reportAttachment: IReportAttachment, index: number) => (
              <IonItemSliding key={reportAttachment.id}>
                <ReportAttachment key={index} reportAttachment={reportAttachment} />
                <IonItemOptions>
                  <IonItemOption color='danger' onClick={(e) => onReportAttachmentDeleteClick(reportAttachment)}>
                    <IonIcon slot='start' icon={trash} />
                    {t('common.buttons.Delete')}
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </>
        )}
        {showAttachments && fillablePDFsList.length > 0 && (
          <>
            <IonItemDivider color='light' className='ion-padding attachment-sub-heading'>
              <IonLabel className='grid-item-divider-label' color='medium'>
                {t('attachments.PDFAttachments')}
              </IonLabel>
            </IonItemDivider>
            {fillablePDFsList.map((pdf: IPDFListItem, index: number) => (
              <IonItemSliding key={pdf.id}>
                <IonItem key={`pdf-item-${pdf.id}`}>
                  <IonIcon slot='start' icon={documentTextOutline} />
                  <IonRow>
                    <IonLabel>{pdf.name}</IonLabel>
                  </IonRow>
                </IonItem>
                <IonItemOptions>
                  <IonItemOption color='danger' onClick={(e) => onPDFDeleteClick(pdf.id)}>
                    <IonIcon slot='start' icon={trash} />
                    {t('common.buttons.Delete')}
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </>
        )}
        {showAttachments && <PulsaraAttachment />}
      </IonList>
    )
  }

  return (
    <IonPage id='AttachmentsPage' data-testid='Attachments'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonTitle>{t('attachments.Attachments')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonItemDivider color='light' className='ion-padding attachment-sub-heading'>
          <IonLabel className='grid-item-divider-label' color='medium'>
            {t('attachments.SelectWhatYouWantToAddToTheChart')}
          </IonLabel>
        </IonItemDivider>
        <IonList lines='full'>
          {isOnNativePlatform && (
            <IonItem>
              <ScanDriversLicenseButton color='primary-alt' />
            </IonItem>
          )}
          {showCloudEkg && (
            <IonItem>
              <EKGImportButton />
            </IonItem>
          )}
          {(customSignatureOptions.length > 0 || isStandardSignatureConfigured) && (
            <IonItem>
              <AddSignatureButton />
            </IonItem>
          )}
          <IonItem>
            <CameraButton />
            <IonLabel className='grid-item-divider-label wrap-text' color='medium'>
              {t('attachments.LimitOfFiveCameraAttachments')}
            </IonLabel>
          </IonItem>
          {isOnNativePlatform && (
            <IonItem>
              <ScanDocumentButton />
              <IonLabel className='grid-item-divider-label wrap-text' color='medium'>
                {t('attachments.LimitOfFiveDocuments')}
              </IonLabel>
            </IonItem>
          )}
          {showSpecialReports && (
            <IonItem>
              <SpecialReportButton />
            </IonItem>
          )}
          {metadata?.length > 0 && (
            <IonItem>
              <FillablePDFButton />
            </IonItem>
          )}
          {/* <IonItem>
            <IonCol className='choose-file-col'>
              <label htmlFor='image-file'>
                <IonIcon className='choose-file-icon' icon={folder} size='large' /> Choose File
                <IonLabel>{fileName}</IonLabel>
              </label>
              <input id='image-file' type='file' onChange={onFileSelection} />
            </IonCol>
            <IonCol>
              <IonButton data-testid='UploadButton' onClick={upload}>
                <IonIcon icon={cloudUpload} /> Upload
              </IonButton>
            </IonCol>
          </IonItem> */}
          <IonItem>
            <IonButtons>
              <IonButton data-testid='DownloadButton' onClick={download} color='primary-alt'>
                <IonIcon icon={codeDownload} /> Display Attachments
              </IonButton>
              <IonButton data-testid='ClearButton' onClick={clear} color='primary-alt'>
                <IonIcon icon={remove} /> Hide
              </IonButton>
            </IonButtons>
          </IonItem>
        </IonList>
        {isLoading && renderLoader()}
        {!isLoading && renderAttachmentsContent()}
        <FillablePDFsModal showModal={showModal} />
      </IonContent>
    </IonPage>
  )
}

export default Attachments
